import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import KeystoneLogo from '../../components/KeystoneLogo';
import ComplianceBand from './ComplianceBand';
import DomTranslator from '../../components/DomTranslator';
import LangPill from '../../components/LangPill';

/** EN/HU/PL/DE switching now happens through <LangPill /> which writes to
 *  the shared `keystone_app_lang` localStorage key. DomTranslator applies
 *  the runtime translation across all pages, so we don't need per-URL
 *  language routes on the marketing header any more. */

export const PALETTE = {
  navy: '#0A2540',
  navyDeep: '#061A2E',
  navyMid: '#163E66',
  magenta: '#E91E63',
  magentaDark: '#C2185B',
  gold: '#C9A961',
  cream: '#FAF8F5',
  paper: '#FFFFFF',
  ink: '#0F1B2A',
  slate: '#3D4A5C',
  muted: '#6B7280',
  border: '#E5E1D8',
  bgSoft: '#F5F1E8',
};

const NAV_ITEMS = [
  { to: '/', label: 'Home', exact: true },
  { to: '/site/how-it-works', label: 'How it works' },
  { to: '/site/features', label: 'Features' },
  { to: '/guides', label: 'Guides' },
  { to: '/site/about', label: 'About' },
  { to: '/site/pricing', label: 'Pricing' },
  { to: '/site/contact', label: 'Contact' },
];

export function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const isActive = (item) => item.exact
    ? location.pathname === item.to
    : location.pathname.startsWith(item.to);

  return (
    <header style={{
      position: 'fixed',
      top: 0, left: 0, right: 0,
      zIndex: 50,
      background: scrolled ? 'rgba(255, 255, 255, 0.95)' : 'rgba(250, 248, 245, 0.85)',
      backdropFilter: 'blur(18px) saturate(180%)',
      WebkitBackdropFilter: 'blur(18px) saturate(180%)',
      borderBottom: `1px solid ${scrolled ? PALETTE.border : 'rgba(229, 225, 216, 0.6)'}`,
      boxShadow: scrolled ? '0 2px 20px rgba(10,37,64,0.06)' : 'none',
      transition: 'all 0.25s ease',
    }}>
      <div style={{
        maxWidth: 1320,
        margin: '0 auto',
        padding: '14px 32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 28,
      }}>
        <Link to="/" style={{ textDecoration: 'none', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          <KeystoneLogo height={46} variant="light" showTagline={false} />
        </Link>

        <nav className="site-nav-desktop" style={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
        }}>
          {NAV_ITEMS.map(item => (
            <Link
              key={item.to}
              to={item.to}
              style={{
                padding: '10px 16px',
                fontSize: 14.5,
                fontWeight: isActive(item) ? 700 : 600,
                color: isActive(item) ? PALETTE.navy : PALETTE.ink,
                textDecoration: 'none',
                borderRadius: 6,
                position: 'relative',
                transition: 'color 0.15s, background 0.15s',
                letterSpacing: '0.005em',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = PALETTE.magenta; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = isActive(item) ? PALETTE.navy : PALETTE.ink; }}
            >
              {item.label}
              {isActive(item) && (
                <span style={{
                  position: 'absolute',
                  bottom: 1,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 18, height: 2.5, borderRadius: 2,
                  background: PALETTE.magenta,
                }} />
              )}
            </Link>
          ))}
        </nav>

        <div className="site-nav-cta" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <LangPill variant="light" />
          <Link
            to="/login"
            data-testid="nav-public-sandbox"
            style={{
              padding: '10px 16px',
              background: 'transparent',
              color: PALETTE.magenta,
              border: `1.5px solid ${PALETTE.magenta}`,
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 700,
              textDecoration: 'none',
              letterSpacing: '0.02em',
            }}
          >
            Try the sandbox
          </Link>
          <Link
            to="/site/contact"
            style={{
              padding: '11px 20px',
              background: PALETTE.navy,
              color: '#fff',
              borderRadius: 6,
              fontSize: 13.5,
              fontWeight: 700,
              textDecoration: 'none',
              letterSpacing: '0.02em',
            }}
          >
            Talk to us →
          </Link>
        </div>

        <button
          className="site-nav-mobile-btn"
          onClick={() => setMobileOpen(!mobileOpen)}
          style={{
            display: 'none',
            background: 'transparent',
            border: 'none',
            padding: 8,
            cursor: 'pointer',
          }}
          aria-label="Menu"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={PALETTE.navy} strokeWidth="2">
            {mobileOpen ? (
              <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>
            ) : (
              <><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></>
            )}
          </svg>
        </button>
      </div>

      {mobileOpen && (
        <>
          <div
            data-testid="mobile-menu-backdrop"
            onClick={() => setMobileOpen(false)}
            style={{ position: 'absolute', top: '100%', left: 0, right: 0, height: '100vh', background: 'rgba(10,37,64,0.45)' }}
          />
          <div data-testid="mobile-menu" style={{
            background: PALETTE.cream,
            borderTop: `1px solid ${PALETTE.border}`,
            padding: '20px 24px 24px',
            maxHeight: 'calc(100dvh - 74px)',
            overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <a href="tel:+353851810310" data-testid="mobile-menu-phone" style={{ fontSize: 14, fontWeight: 700, color: PALETTE.navy, textDecoration: 'none' }}>+353 85 181 0310</a>
              <LangPill variant="light" />
            </div>
            {NAV_ITEMS.map(item => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                style={{
                  display: 'block',
                  padding: '14px 0',
                  fontSize: 17,
                  fontWeight: isActive(item) ? 700 : 500,
                  color: isActive(item) ? PALETTE.navy : PALETTE.slate,
                  textDecoration: 'none',
                  borderBottom: `1px solid ${PALETTE.border}`,
                }}
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/site/contact"
              onClick={() => setMobileOpen(false)}
              style={{
                display: 'block',
                marginTop: 16,
                padding: '14px',
                background: PALETTE.navy,
                color: '#fff',
                textAlign: 'center',
                borderRadius: 6,
                fontSize: 14,
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              Talk to us →
            </Link>
          </div>
        </>
      )}

      <style>{`
        @media (max-width: 900px) {
          .site-nav-desktop, .site-nav-cta { display: none !important; }
          .site-nav-mobile-btn { display: block !important; }
        }
      `}</style>
    </header>
  );
}

export function SiteFooter() {
  const year = new Date().getFullYear();
  const linkStyle = {
    display: 'block', fontSize: 12.5, color: '#94A3B8',
    textDecoration: 'none', padding: '3px 0', lineHeight: 1.4,
  };
  const colTitle = {
    fontSize: 10.5, letterSpacing: '0.22em', textTransform: 'uppercase',
    fontWeight: 700, color: PALETTE.gold, marginBottom: 14,
  };
  return (
    <footer className="site-footer" style={{
      background: PALETTE.navyDeep,
      color: '#CBD5E1',
      padding: '64px 32px 32px',
      paddingBottom: 'calc(32px + var(--sticky-cta-pad, 0px))',
      marginTop: 0,
    }}>
      {/* ───── MEGA SITEMAP, all SEO landing pages, grouped by intent ───── */}
      <div style={{
        maxWidth: 1280,
        margin: '0 auto 56px',
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: 40,
        paddingBottom: 40,
        borderBottom: '1px solid rgba(255,255,255,0.08)',
      }} className="site-footer-mega">
        <div>
          <div style={colTitle}>By industry</div>
          <Link to="/dairy-traceability-software-ireland" style={linkStyle}>Dairy traceability (Ireland)</Link>
          <Link to="/cheese-production-software" style={linkStyle}>Cheese production</Link>
          <Link to="/bakery-traceability-software" style={linkStyle}>Bakery traceability</Link>
          <Link to="/meat-processing-traceability-software" style={linkStyle}>Meat processing</Link>
          <Link to="/seafood-traceability-software" style={linkStyle}>Seafood & fish</Link>
          <Link to="/prepared-food-manufacturing-software" style={linkStyle}>Prepared food & ready meals</Link>
          <Link to="/food-wholesale-traceability-software" style={linkStyle}>Wholesale & distribution</Link>
        </div>
        <div>
          <div style={colTitle}>By feature</div>
          <Link to="/batch-traceability-software" style={linkStyle}>Batch traceability</Link>
          <Link to="/batch-tracking-software-cheese-producers" style={linkStyle}>Cheese batch tracking</Link>
          <Link to="/coa-generator-food-manufacturing" style={linkStyle}>COA generator</Link>
          <Link to="/food-production-planning-software-ireland" style={linkStyle}>Weekly production planner</Link>
          <Link to="/customer-sku-mapping-food-manufacturing" style={linkStyle}>Customer SKU mapping</Link>
          <Link to="/food-dispatch-note-software" style={linkStyle}>Dispatch notes</Link>
          <Link to="/pallet-label-software-food-manufacturing" style={linkStyle}>Pallet labels</Link>
          <Link to="/retailer-dispatch-software-food-producers" style={linkStyle}>Retailer dispatch</Link>
        </div>
        <div>
          <div style={colTitle}>By compliance</div>
          <Link to="/food-traceability-software-ireland" style={linkStyle}>Food traceability (Ireland)</Link>
          <Link to="/food-traceability-software-uk" style={linkStyle}>Food traceability (UK)</Link>
          <Link to="/brcgs-traceability-software" style={linkStyle}>BRCGS traceability</Link>
          <Link to="/salsa-certification-software" style={linkStyle}>SALSA certification</Link>
          <Link to="/haccp-traceability-software" style={linkStyle}>HACCP traceability</Link>
          <Link to="/mock-recall-software-food-producers" style={linkStyle}>Mock recall (90 sec)</Link>
          <Link to="/food-recall-management-software" style={linkStyle}>Recall management</Link>
          <Link to="/allergen-management-food-manufacturing" style={linkStyle}>Allergen management</Link>
          <Link to="/irish-food-producer-audit-readiness-checklist" style={linkStyle}>Audit-readiness checklist</Link>
        </div>
        <div>
          <div style={colTitle}>By integration</div>
          <Link to="/accountsiq-keystone" style={linkStyle}>AccountsIQ + Keystone</Link>
          <Link to="/sage-keystone" style={linkStyle}>Sage + Keystone</Link>
          <div style={{ ...colTitle, marginTop: 28 }}>Comparisons</div>
          <Link to="/excel-vs-keystone" style={linkStyle}>Excel vs Keystone</Link>
          <Link to="/keystone-vs-competitors" style={linkStyle}>vs all competitors (overview)</Link>
          <Link to="/vs-softtrace" style={linkStyle}>vs SoftTrace</Link>
          <Link to="/vs-csb" style={linkStyle}>vs CSB-System</Link>
          <Link to="/vs-fooddocs" style={linkStyle}>vs FoodDocs</Link>
          <Link to="/vs-diomac" style={linkStyle}>vs DIOMAC & Herbst</Link>
          <div style={{ ...colTitle, marginTop: 28 }}>Alternatives</div>
          <Link to="/softtrace-alternative" style={linkStyle}>SoftTrace alternative</Link>
          <Link to="/mrpeasy-alternative" style={linkStyle}>MRPeasy alternative</Link>
          <Link to="/wherefour-alternative" style={linkStyle}>Wherefour alternative</Link>
        </div>
        <div>
          <div style={colTitle}>Guides</div>
          <Link to="/guides" style={linkStyle}>All guides</Link>
          <Link to="/guides/how-to-run-a-mock-recall" style={linkStyle}>Run a mock recall</Link>
          <Link to="/guides/brcgs-issue-9-traceability-requirements" style={linkStyle}>BRCGS traceability</Link>
          <Link to="/guides/traceability-test-mass-balance" style={linkStyle}>The 4-hour mass balance</Link>
          <Link to="/guides/fsai-inspection-preparation" style={linkStyle}>FSAI inspection prep</Link>
          <div style={{ ...colTitle, marginTop: 28 }}>Resources</div>
          <Link to="/case-study/cashel-blue-story" style={linkStyle}>Cashel Blue case study</Link>
          <Link to="/founder-led-implementation" style={linkStyle}>Senior-led implementation</Link>
          <Link to="/site/features" style={linkStyle}>Full feature list</Link>
          <Link to="/site/how-it-works" style={linkStyle}>How it works (PO → invoice)</Link>
          <Link to="/site/pricing" style={linkStyle}>Pricing</Link>
          <Link to="/site/about" style={linkStyle}>About the team</Link>
          <a href="/sitemap.xml" style={linkStyle}>XML sitemap</a>
          <a href="/robots.txt" style={linkStyle}>robots.txt</a>
        </div>
      </div>

      {/* ───── Primary footer columns ───── */}
      <div style={{
        maxWidth: 1280,
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: '1.5fr 1fr 1fr 1fr',
        gap: 48,
        marginBottom: 48,
      }} className="site-footer-grid">
        <div>
          <KeystoneLogo height={40} variant="dark" showTagline={false} />
          <p style={{ fontSize: 14, lineHeight: 1.6, color: '#94A3B8', marginTop: 16, maxWidth: 280 }}>
            Production software for European artisan food manufacturers. Built by people who've stood on the wet end of a cheese line at six in the morning.
          </p>
        </div>
        <div>
          <div style={{ fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.gold, marginBottom: 14 }}>Keystone</div>
          {[
            { to: '/', label: 'Home' },
            { to: '/site/about', label: 'About' },
            { to: '/press', label: 'Press & media' },
            { to: '/site/pricing', label: 'Pricing' },
            { to: '/site/contact', label: 'Contact' },
            { to: '/site/privacy', label: 'Privacy' },
            { to: '/site/security', label: 'Security' },
            { to: '/site/legal', label: 'Legal & disclosures' },
            { to: '/legal/permissions', label: 'Permissions' },
            { to: '/legal/sub-processors', label: 'Sub-processors' },
            { to: '/login', label: 'Try the sandbox' },
          ].map(l => (
            <Link key={l.to} to={l.to} style={{ display: 'block', fontSize: 13, color: '#CBD5E1', textDecoration: 'none', padding: '4px 0' }}>
              {l.label}
            </Link>
          ))}
        </div>
        <div>
          <div style={{ fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.gold, marginBottom: 14 }}>Contact</div>
          <a href="mailto:hello@cloudvoro.com" style={{ display: 'block', fontSize: 13, color: '#CBD5E1', textDecoration: 'none', padding: '4px 0' }}>hello@cloudvoro.com</a>
          <a href="tel:+353851810310" style={{ display: 'block', fontSize: 13, color: '#CBD5E1', textDecoration: 'none', padding: '4px 0' }}>+353 85 181 0310</a>
          <Link to="/site/contact" style={{ display: 'block', fontSize: 13, color: '#CBD5E1', textDecoration: 'none', padding: '4px 0' }}>Book a call</Link>
        </div>
        <div>
          <div style={{ fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.gold, marginBottom: 14 }}>Compliance</div>
          {['Hosted in the EU / Ireland', 'GDPR · Data Protection Lead assigned', 'Designed with NIS2-aligned controls', 'Encrypted at rest & in transit', 'Supports FSAI · BRCGS · EU food safety regulations'].map(l => (
            <div key={l} style={{ fontSize: 13, color: '#94A3B8', padding: '4px 0' }}>{l}</div>
          ))}
        </div>
      </div>
      <div style={{
        maxWidth: 1280,
        margin: '0 auto',
        paddingTop: 24,
        borderTop: '1px solid rgba(255,255,255,0.08)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 16,
        fontSize: 12,
        color: '#64748B',
      }}>
        <span>© {year} CloudVoro (Ireland), A sister company to Intellix IT Solutions (UK).</span>
        <span style={{ color: PALETTE.gold, letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 700 }}>Keystone · Production Systems</span>
      </div>
      <style>{`
        @media (max-width: 1100px) {
          .site-footer-mega { grid-template-columns: repeat(3, 1fr) !important; gap: 32px !important; }
        }
        @media (max-width: 800px) {
          .site-footer-grid { grid-template-columns: 1fr 1fr !important; gap: 32px !important; }
          .site-footer-mega { grid-template-columns: 1fr 1fr !important; gap: 28px !important; }
        }
        @media (max-width: 500px) {
          .site-footer-grid { grid-template-columns: 1fr !important; }
          .site-footer-mega { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </footer>
  );
}

export default function SiteLayout({ children }) {
  return (
    <div className="site-shell" style={{
      background: PALETTE.cream,
      color: PALETTE.ink,
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      minHeight: '100vh',
      lineHeight: 1.5,
    }}>
      <DomTranslator />
      <SiteHeader />
      <main>{children}</main>
      <ComplianceBand />
      <SiteFooter />

      {/* ── Sticky mobile CTA bar ─────────────────────────────────────────
          Bottom-anchored on ≤740px viewports. Two actions: Phone (tel:),
          Book (routes to /site/contact). Hidden on desktop where the
          header nav already carries these. The <main> pads its bottom by
          68px so the bar never obscures the last piece of content. */}
      <div className="site-mobile-cta" data-testid="mobile-sticky-cta" role="navigation" aria-label="Primary contact actions">
        <a
          href="tel:+353851810310"
          data-testid="mobile-sticky-cta-phone"
          aria-label="Phone the Keystone team directly"
          className="site-mobile-cta-btn site-mobile-cta-phone"
        >
          <span className="site-mobile-cta-icon" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/>
            </svg>
          </span>
          Phone
        </a>
        <Link
          to="/site/contact"
          data-testid="mobile-sticky-cta-book"
          aria-label="Book a 20-minute call"
          className="site-mobile-cta-btn site-mobile-cta-book"
        >
          <span className="site-mobile-cta-icon" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
          </span>
          Book
        </Link>
      </div>

      {/* ── Site-wide responsive safety net ──
          Runs after all inline styles/@media rules; prevents any single
          section from breaking horizontal layout on phones/tablets.
          Kept minimal so it doesn't fight per-page media queries. */}
      <style>{`
        html, body { overflow-x: hidden; }
        .site-shell img, .site-shell video, .site-shell svg, .site-shell iframe {
          max-width: 100%;
          height: auto;
        }
        .site-shell table { max-width: 100%; }
        @media (max-width: 900px) {
          .site-shell h1 { word-break: break-word; }
          .site-shell h2, .site-shell h3 { word-break: break-word; }
          .site-shell h3 { min-width: 0; }
        }
        @media (max-width: 740px) {
          .site-shell { --sticky-cta-pad: 72px; }
          html { scroll-padding-bottom: 96px; }
          .site-shell button, .site-shell a { scroll-margin-bottom: 96px; }
          .site-shell table th, .site-shell table td { padding: 10px 10px !important; font-size: 12px !important; }
          .site-shell section { padding-left: 20px !important; padding-right: 20px !important; }
          .site-shell footer { padding-left: 20px !important; padding-right: 20px !important; }
          .site-shell input, .site-shell select, .site-shell textarea { font-size: 16px !important; }
        }

        /* Sticky mobile CTA, hidden by default, shown ≤ 740px */
        .site-mobile-cta { display: none; }
        @media (max-width: 740px) {
          .site-shell { --sticky-cta-pad: 72px; }
          .site-mobile-cta {
            position: fixed;
            left: 8px;
            right: 8px;
            bottom: 8px;
            z-index: 50;
            display: grid;
            grid-template-columns: 1fr 1.15fr 1fr;
            gap: 6px;
            padding: 6px;
            background: rgba(10, 37, 64, 0.96);
            backdrop-filter: blur(8px);
            -webkit-backdrop-filter: blur(8px);
            border: 1px solid rgba(255, 255, 255, 0.12);
            border-radius: 14px;
            box-shadow: 0 8px 32px rgba(10, 37, 64, 0.30), 0 2px 6px rgba(10, 37, 64, 0.20);
          }
          .site-mobile-cta-btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            padding: 11px 4px;
            border-radius: 10px;
            font-size: 12.5px;
            font-weight: 700;
            letter-spacing: 0.03em;
            text-decoration: none;
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            -webkit-tap-highlight-color: transparent;
            transition: transform 0.12s ease, background 0.12s ease;
          }
          .site-mobile-cta-btn:active { transform: scale(0.97); }
          .site-mobile-cta-icon { display: inline-flex; align-items: center; }
          .site-mobile-cta-phone {
            background: rgba(255, 255, 255, 0.08);
            color: #fff;
            border: 1px solid rgba(255, 255, 255, 0.14);
          }
          .site-mobile-cta-book {
            background: ${PALETTE.magenta};
            color: #fff;
          }
          .site-mobile-cta-brochure {
            background: ${PALETTE.gold};
            color: ${PALETTE.navy};
          }
          @media (max-width: 380px) {
            .site-mobile-cta-btn { font-size: 11.5px; padding: 10px 2px; }
          }
        }
        /* Never show the sticky bar in print or on the sales brochure route
           (that page has its own top control bar). */
        @media print { .site-mobile-cta { display: none !important; } }
      `}</style>
    </div>
  );
}
